import React from 'react';
import { observer } from 'mobx-react-lite';
import { OldInput } from 'src/domains/layouts/shared/formComponents/input/OldInput';
import { OldTextarea } from 'src/domains/layouts/shared/formComponents/textarea/OldTextarea';
import { useAppStateContext } from 'src/AppState/AppState';
import DatePicker from 'react-datepicker';
import { FormHeader } from 'src/domains/layouts/shared/cms/form/formHeader/FormHeader';
import { LandingPageState } from 'src/AppState/LandingPageState/LandingPageState';
import {
    ErrorMessageBar,
    InputError,
    InputLabel,
} from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import {
    FormComponent,
    FormWrapper,
    DatesWrapper,
    DateInputWrapper,
} from 'src/domains/layouts/shared/cms/form/Form.style';
import { Label } from 'src/domains/layouts/shared/typography/Content.style';
import { Banners } from 'src/components/LandingPage/Banners/Banners';
import { lpActionTemplateOptions, lpFrequencyShowOptions } from './selectionOptions';
import { observable, makeAutoObservable } from 'mobx';
import { Toggle } from 'src/domains/layouts/shared/toggle/Toggle';
import { FormButtons } from 'src/domains/layouts/shared/buttons/FormButtons';
import { OldSelect } from 'src/domains/layouts/shared/formComponents/Select/components/OldSelect';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';

class State {
    public constructor(private readonly landingPageState: LandingPageState) {
        makeAutoObservable(this);
    }
    @observable public startDateError = false;
    @observable public endDateError = false;
    @observable public showDirectLinkField = false;

    public onChangeHomepageSwitch = async (): Promise<void> => {
        const isHomepage = this.landingPageState.isHomepage;
        isHomepage.setValue(!isHomepage.value);
        if (isHomepage.value === true) {
            this.landingPageState.slug.setValue('homepage');
        } else {
            this.landingPageState.slug.setValue('');
        }
    };

    public onChangeStartDate = (date: Date | null): void => {
        if (date !== null) {
            this.landingPageState.dateFrom.setValue(date.toISOString());
        }
    };

    public onBlurStartDate = (date: React.FocusEvent<HTMLInputElement>): void => {
        if (date.target.value === '') {
            this.startDateError = true;
        } else if (this.startDateError) {
            this.startDateError = false;
        }
    };

    public onChangeEndDate = (date: Date | null): void => {
        if (date !== null) {
            this.landingPageState.dateTo.setValue(date.toISOString());
        }
    };

    public onBlurEndDate = (date: React.FocusEvent<HTMLInputElement>): void => {
        if (date.target.value === '') {
            this.endDateError = true;
        } else if (this.endDateError) {
            this.endDateError = false;
        }
    };
}

export const LandingPageForm = observer(() => {
    const { landingPageState } = useAppStateContext();
    const [state] = React.useState(() => new State(landingPageState));
    const parsedStartDate =
        // eslint-disable-next-line
        landingPageState.dateFrom.value === null ? null : Date.parse(landingPageState.dateFrom.value);
    const startDate = parsedStartDate === null || isNaN(parsedStartDate) ? null : new Date(parsedStartDate);
    // eslint-disable-next-line
    const parsedEndDate = landingPageState.dateTo.value === null ? null : Date.parse(landingPageState.dateTo.value);
    const endDate = parsedEndDate === null || isNaN(parsedEndDate) ? null : new Date(parsedEndDate);

    return (
        <FormWrapper>
            <FormHeader
                isEditMode={landingPageState.isEditMode}
                defaultLabel='Create new landing page'
                editModeLabel='Edit landing page'
                resetForm={landingPageState.setDefaultState}
            />
            <FormComponent onSubmit={(e): void => e.preventDefault()}>
                <Loader isActive={landingPageState.isLoading} />

                {Boolean(landingPageState.errorMessage) && (
                    <ErrorMessageBar>{landingPageState.errorMessage}</ErrorMessageBar>
                )}

                <Toggle
                    label='Set as homepage Landing Page without slug'
                    toggleActive={state.onChangeHomepageSwitch}
                    isActive={landingPageState.isHomepage.value}
                    isDisabled={landingPageState.isEditMode}
                    dataTest='lp-slug-radio-button'
                />
                {landingPageState.isHomepage.value === false ? (
                    <OldInput
                        label='Slug*'
                        status={landingPageState.slugStatus}
                        formInputState={landingPageState.slug}
                        model={landingPageState.slugModel}
                        readOnly={landingPageState.isEditMode}
                        dataTest='set-lp-slug'
                    />
                ) : null}

                <OldInput
                    label='LP Name: (Max 30 characters)*'
                    status={landingPageState.titleStatus}
                    formInputState={landingPageState.title}
                    model={landingPageState.titleModel}
                    dataTest='set-lp-name'
                />
                <OldInput
                    label={landingPageState.isEditMode ? 'Edit Promo Code*' : 'Create New Promo Code*'}
                    status={landingPageState.promoCodeStatus}
                    formInputState={landingPageState.promoCode}
                    model={landingPageState.promoCodeModel}
                    dataTest='set-lp-promo-code'
                />

                <OldSelect
                    dataTest='set-lp-times-shown'
                    selectOptions={lpFrequencyShowOptions}
                    stateValue={landingPageState.frequencyShow}
                />

                <DatesWrapper>
                    <DateInputWrapper>
                        <InputLabel>Start date *</InputLabel>
                        <DatePicker
                            selected={startDate}
                            onChange={state.onChangeStartDate}
                            onBlur={state.onBlurStartDate}
                            showTimeSelect
                            timeFormat='HH:mm'
                            timeIntervals={15}
                            timeCaption='time'
                            dateFormat='MMMM d, yyyy h:mm aa'
                            id='Start-date'
                        />
                        {state.startDateError ? <InputError>This field is required.</InputError> : null}
                    </DateInputWrapper>
                    <DateInputWrapper>
                        <InputLabel>End date *</InputLabel>
                        <DatePicker
                            selected={endDate}
                            onChange={state.onChangeEndDate}
                            onBlur={state.onBlurEndDate}
                            showTimeSelect
                            timeFormat='HH:mm'
                            timeIntervals={15}
                            timeCaption='time'
                            dateFormat='MMMM d, yyyy h:mm aa'
                            id='End-date'
                        />
                        {state.endDateError ? <InputError>This field is required.</InputError> : null}
                    </DateInputWrapper>
                </DatesWrapper>

                <OldInput
                    label='Call to action: (Max 30 characters)*'
                    status={landingPageState.ctaTextStatus}
                    formInputState={landingPageState.ctaText}
                    model={landingPageState.ctaTextModel}
                    dataTest='set-lp-call-to-action'
                />

                <OldSelect
                    selectOptions={lpActionTemplateOptions}
                    stateValue={landingPageState.actionTemplate}
                    dataTest='lp-redirection-dropdown'
                />

                {landingPageState.isDirectLinkSelected ? (
                    <OldInput
                        label='Call to action destination link'
                        status={landingPageState.ctaUrlStatus}
                        formInputState={landingPageState.ctaUrl}
                        model={landingPageState.ctaUrlModel}
                        dataTest='lp-call-to-action-link'
                    />
                ) : null}

                <Banners />

                <OldTextarea
                    label='Terms and conditions'
                    formState={landingPageState.tcText}
                    model={landingPageState.tcTextModel}
                    status={landingPageState.tcTextStatus}
                    ckeditor
                />

                <OldTextarea
                    label='Manually add users to this promotion'
                    placeholder='Use comma to separate user ids'
                    formState={landingPageState.idsList}
                    model={landingPageState.idsListModel}
                    status={landingPageState.idsListStatus}
                />

                <Label>Total Customers Added: {landingPageState.addedIds.length}</Label>

                <FormButtons
                    onCreate={landingPageState.handleSubmit}
                    onUpdate={landingPageState.handleSubmit}
                    onCancel={landingPageState.handleCancel}
                    isEditMode={landingPageState.isEditMode}
                    isCreateDisabled={!landingPageState.isFormValid}
                    isUpdateDisabled={!landingPageState.isFormValid}
                    dataTestCreate='create-lp-button'
                    dataTestUpdate='create-lp-button'
                />
            </FormComponent>
        </FormWrapper>
    );
});
